import React, { useState, useMemo } from "react";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Menu,
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Divider,
  Typography,
  Tooltip,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FilterListIcon from "@mui/icons-material/FilterList";
import { cellValue, formatValue, sumCells } from "../render/valueResolver";

// Interactive, Excel-like table: each header sorts (click label) and filters
// (funnel icon → checkbox list of the column's distinct values).

const MONO = 'ui-monospace, "SF Mono", Menlo, Consolas, monospace';

function compareVals(a, b, type) {
  if (type === "number" || type === "currency") {
    const na = Number(a);
    const nb = Number(b);
    if (!isNaN(na) && !isNaN(nb)) return na - nb;
  }
  if (type === "date") {
    const da = Date.parse(String(a).replace(" ", "T"));
    const db = Date.parse(String(b).replace(" ", "T"));
    if (!isNaN(da) && !isNaN(db)) return da - db;
  }
  return String(a == null ? "" : a).localeCompare(String(b == null ? "" : b));
}

export default function PreviewDataTable({ columns, rows, showTotals }) {
  const [sort, setSort] = useState(null); // { col, dir }
  const [filters, setFilters] = useState({}); // { colIndex: Set<displayValue> }
  const [menu, setMenu] = useState(null); // { col, anchor }
  const [search, setSearch] = useState("");

  const disp = (row, col) => formatValue(cellValue(row, col), col.type);

  const filteredRows = useMemo(() => {
    let out = rows.filter((row) =>
      columns.every((col, ci) => {
        const f = filters[ci];
        return !f || f.has(disp(row, col));
      })
    );
    if (sort) {
      const col = columns[sort.col];
      out = [...out].sort((r1, r2) => {
        const c = compareVals(cellValue(r1, col), cellValue(r2, col), col.type);
        return sort.dir === "asc" ? c : -c;
      });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, columns, filters, sort]);

  const toggleSort = (ci) =>
    setSort((s) => {
      if (!s || s.col !== ci) return { col: ci, dir: "asc" };
      if (s.dir === "asc") return { col: ci, dir: "desc" };
      return null;
    });

  const distinctValues = useMemo(() => {
    if (!menu) return [];
    const col = columns[menu.col];
    const set = new Set();
    rows.forEach((row) => set.add(disp(row, col)));
    return Array.from(set).sort((a, b) => compareVals(a, b, col.type));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu, rows, columns]);

  const activeSet = menu ? filters[menu.col] : null;
  const isChecked = (val) => (activeSet ? activeSet.has(val) : true);

  const toggleValue = (val) => {
    if (!menu) return;
    const ci = menu.col;
    setFilters((prev) => {
      const cur = prev[ci] ? new Set(prev[ci]) : new Set(distinctValues);
      if (cur.has(val)) cur.delete(val);
      else cur.add(val);
      if (cur.size === distinctValues.length) {
        const cp = { ...prev };
        delete cp[ci];
        return cp;
      }
      return { ...prev, [ci]: cur };
    });
  };

  const selectAll = () => {
    if (!menu) return;
    setFilters((p) => {
      const cp = { ...p };
      delete cp[menu.col];
      return cp;
    });
  };
  const clearAll = () => menu && setFilters((p) => ({ ...p, [menu.col]: new Set() }));

  const shownValues = distinctValues.filter((v) => v.toLowerCase().includes(search.toLowerCase()));

  return (
    <Box sx={{ overflowX: "auto", overscrollBehaviorX: "contain" }}>
      <Table size="small" stickyHeader sx={{ "& td, & th": { fontSize: 12.5 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700, bgcolor: "background.paper", width: 44, color: "text.secondary" }}>#</TableCell>
            {columns.map((col, ci) => {
              const sorted = sort && sort.col === ci;
              const filtered = !!filters[ci];
              return (
                <TableCell
                  key={ci}
                  align={col.align === "right" ? "right" : "left"}
                  sx={{ fontWeight: 700, whiteSpace: "nowrap", bgcolor: "background.paper" }}
                >
                  <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.25 }}>
                    <Box component="span" onClick={() => toggleSort(ci)} sx={{ cursor: "pointer" }}>
                      {col.header}
                    </Box>
                    {sorted &&
                      (sort.dir === "asc" ? (
                        <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                      ) : (
                        <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                      ))}
                    <Tooltip title="Filter">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setSearch("");
                          setMenu({ col: ci, anchor: e.currentTarget });
                        }}
                        sx={{ p: 0.25, color: filtered ? "primary.main" : "text.disabled" }}
                      >
                        <FilterListIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredRows.length ? (
            filteredRows.map((row, ri) => (
              <TableRow key={ri} hover>
                <TableCell sx={{ color: "text.disabled", fontFamily: MONO }}>{ri + 1}</TableCell>
                {columns.map((col, ci) => {
                  const v = disp(row, col);
                  return (
                    <TableCell key={ci} align={col.align === "right" ? "right" : "left"} sx={{ fontFamily: MONO }}>
                      {v === "" ? "—" : v}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length + 1} align="center" sx={{ color: "text.disabled", py: 3 }}>
                No matching rows
              </TableCell>
            </TableRow>
          )}
          {showTotals && filteredRows.length > 0 && (
            <TableRow>
              <TableCell sx={{ fontWeight: 800, bgcolor: "action.hover", position: "sticky", bottom: 0 }}>TOTAL</TableCell>
              {columns.map((col, ci) => (
                <TableCell
                  key={ci}
                  align={col.align === "right" ? "right" : "left"}
                  sx={{ fontWeight: 800, bgcolor: "action.hover", position: "sticky", bottom: 0 }}
                >
                  {col.total ? formatValue(sumCells(filteredRows, col), col.type) : ""}
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Menu anchorEl={menu ? menu.anchor : null} open={!!menu} onClose={() => setMenu(null)}>
        <Box sx={{ px: 1.5, py: 1, width: 250 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search values"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: "flex", gap: 1, mb: 0.5 }}>
            <Button size="small" onClick={selectAll}>
              Select all
            </Button>
            <Button size="small" onClick={clearAll}>
              Clear
            </Button>
          </Box>
          <Divider />
          <Box sx={{ maxHeight: 260, overflowY: "auto", mt: 0.5 }}>
            {shownValues.length ? (
              shownValues.map((val) => (
                <FormControlLabel
                  key={val}
                  sx={{ display: "flex", m: 0 }}
                  control={<Checkbox size="small" checked={isChecked(val)} onChange={() => toggleValue(val)} />}
                  label={
                    <Typography variant="body2" sx={{ fontFamily: MONO }}>
                      {val === "" ? "(blank)" : val}
                    </Typography>
                  }
                />
              ))
            ) : (
              <Typography variant="caption" color="text.disabled">
                No values
              </Typography>
            )}
          </Box>
        </Box>
      </Menu>
    </Box>
  );
}
